"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { playSound } from "@/lib/game/audio";
import { pickSpellingWord } from "@/lib/game/activities";
import { formatStarReward } from "@/lib/game/stars";
import { ActivityLayout } from "./ActivityLayout";
import { ActivitySuccessOverlay } from "./ActivitySuccessOverlay";
import { DifficultyPicker } from "./DifficultyPicker";
import { DragChip, DropSlot } from "./DragChip";
import { DragDropProvider } from "./DragDropContext";
import { GameIconButton } from "../game-ui";
import { useGame } from "../GameProvider";

const DECOYS = "abcdefghijklmnopqrstuvwxyz";

function shuffle<T>(arr: T[]): T[] {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j]!, copy[i]!];
  }
  return copy;
}

function buildLetterBank(word: string, difficulty: "easy" | "medium" | "hard") {
  const items = word.split("").map((letter, i) => ({
    id: `w-${i}`,
    letter,
  }));
  const extra = difficulty === "easy" ? 0 : difficulty === "medium" ? 2 : 3;
  const decoys: { id: string; letter: string }[] = [];
  let di = 0;
  while (decoys.length < extra) {
    const c = DECOYS[Math.floor(Math.random() * DECOYS.length)]!;
    if (!word.includes(c)) {
      decoys.push({ id: `d-${di++}`, letter: c });
    }
  }
  return shuffle([...items, ...decoys]);
}

function SpellingGame({
  word,
  hint,
  onPlayAgain,
}: {
  word: string;
  hint: string;
  onPlayAgain: () => void;
}) {
  const { completeActivity, progress } = useGame();
  const activityId = "spelling";
  const difficulty = progress.difficulty;

  const slots = word.length;
  const [slotChips, setSlotChips] = useState<Record<string, string>>({});
  const [usedIds, setUsedIds] = useState<Set<string>>(new Set());
  const [feedback, setFeedback] = useState<"idle" | "wrong" | "right">("idle");
  const completedRef = useRef(false);
  const lastJudgedRef = useRef("");

  const locked = feedback === "right";
  const bank = useMemo(
    () => buildLetterBank(word, difficulty),
    [word, difficulty],
  );
  const bankById = useMemo(
    () => Object.fromEntries(bank.map((b) => [b.id, b.letter])),
    [bank],
  );

  useEffect(() => {
    completedRef.current = false;
    lastJudgedRef.current = "";
    setSlotChips({});
    setUsedIds(new Set());
    setFeedback("idle");
  }, [word]);

  const currentWord = Array.from(
    { length: slots },
    (_, i) => bankById[slotChips[`slot-${i}`] ?? ""] ?? "",
  ).join("");

  useEffect(() => {
    if (locked || completedRef.current) return;
    if (Object.keys(slotChips).length < slots) {
      if (feedback === "wrong") setFeedback("idle");
      lastJudgedRef.current = "";
      return;
    }

    if (currentWord === lastJudgedRef.current) return;
    lastJudgedRef.current = currentWord;

    if (currentWord.toLowerCase() === word.toLowerCase()) {
      completedRef.current = true;
      setFeedback("right");
      playSound("correct");
      completeActivity(activityId, difficulty);
    } else {
      setFeedback("wrong");
      playSound("wrong");
    }
  }, [
    slotChips,
    currentWord,
    slots,
    word,
    locked,
    feedback,
    activityId,
    difficulty,
    completeActivity,
  ]);

  const handleDrop = useCallback(
    (value: string, slotId: string | null) => {
      if (locked) return;
      if (!bankById[value]) return;

      if (!slotId?.startsWith("slot-")) return;
      if (slotChips[slotId] || usedIds.has(value)) return;

      setSlotChips((prev) => ({ ...prev, [slotId]: value }));
      setUsedIds((prev) => new Set(prev).add(value));
    },
    [locked, bankById, slotChips, usedIds],
  );

  const handlePickFromSlot = useCallback(
    (slotId: string) => {
      if (locked) return;
      const chipId = slotChips[slotId];
      if (!chipId) return;

      setSlotChips((prev) => {
        const next = { ...prev };
        delete next[slotId];
        return next;
      });
      setUsedIds((prev) => {
        const next = new Set(prev);
        next.delete(chipId);
        return next;
      });
      lastJudgedRef.current = "";
      setFeedback("idle");
    },
    [locked, slotChips],
  );

  const handleReset = () => {
    if (locked) return;
    setSlotChips({});
    setUsedIds(new Set());
    lastJudgedRef.current = "";
    setFeedback("idle");
  };

  return (
    <DragDropProvider onDrop={handleDrop}>
      <div className="relative flex h-full min-h-0 w-full flex-col overflow-hidden bg-sky-50">
        <div className="shrink-0 px-4 py-3 text-center">
          <p className="text-2xl font-black text-sky-900 sm:text-3xl">{hint}</p>
        </div>

        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-4 overflow-hidden px-4">
          <div className="flex justify-center gap-1.5 sm:gap-2">
            {Array.from({ length: slots }, (_, i) => {
              const slotId = `slot-${i}`;
              const chipId = slotChips[slotId];
              return (
                <DropSlot
                  key={i}
                  id={slotId}
                  value={chipId ? bankById[chipId]?.toUpperCase() ?? null : null}
                  dragValue={chipId ?? null}
                  editable={!locked}
                  onPickFromSlot={handlePickFromSlot}
                  className="h-12 w-10 text-base uppercase sm:h-14 sm:w-12 sm:text-lg"
                />
              );
            })}
          </div>

          <div className="flex max-h-full flex-wrap justify-center gap-1.5 overflow-y-auto sm:gap-2">
            {bank.map((item) => (
              <DragChip
                key={item.id}
                value={item.id}
                label={item.letter.toUpperCase()}
                disabled={locked || usedIds.has(item.id)}
                className="h-11 w-11 text-base uppercase sm:h-12 sm:w-12 sm:text-lg"
              />
            ))}
          </div>
        </div>

        <div className="shrink-0 space-y-2 border-t border-sky-200 bg-sky-50/95 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
          {feedback === "wrong" && (
            <p className="text-center text-sm font-semibold text-rose-600">✗</p>
          )}

          {!locked && Object.keys(slotChips).length > 0 && (
            <div className="flex justify-center">
              <GameIconButton
                icon="clear"
                tone="slate"
                size="md"
                label="Clear all letters"
                onClick={handleReset}
              />
            </div>
          )}
        </div>

        <ActivitySuccessOverlay
          show={feedback === "right"}
          title="Super spelling!"
          theme="sky"
          rewardLabel={formatStarReward(difficulty)}
          onPlayAgain={onPlayAgain}
        />
      </div>
    </DragDropProvider>
  );
}

export function SpellingActivity() {
  const { progress, setDifficulty, trackActivitySession } = useGame();
  const [challengeKey, setChallengeKey] = useState(0);

  useEffect(() => {
    trackActivitySession("spelling");
  }, [trackActivitySession]);

  const challenge = useMemo(
    () => pickSpellingWord(progress.difficulty),
    [progress.difficulty, challengeKey],
  );

  const handleNewWord = () => setChallengeKey((k) => k + 1);

  return (
    <ActivityLayout
      title="Space Words"
      headerExtra={
        <div className="flex items-center gap-2">
          <p className="shrink-0 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Difficulty
          </p>
          <DifficultyPicker
            value={progress.difficulty}
            onChange={(level) => {
              setDifficulty(level);
              handleNewWord();
            }}
            compact
          />
        </div>
      }
    >
      <SpellingGame
        key={`${progress.difficulty}-${challengeKey}-${challenge.word}`}
        word={challenge.word}
        hint={challenge.hint}
        onPlayAgain={handleNewWord}
      />
    </ActivityLayout>
  );
}
